import {
  Box,
  Avatar as MuiAvatar,
  type Theme,
  avatarClasses,
  styled,
} from "@mui/material"
import {
  ROLES,
  type RoleStrategy,
  useRoleConf,
  useSsoService,
  useStepUpToken,
} from "@pharmupp/p3-front-commons"
import { type MouseEventHandler, useState } from "react"
import { Lock, Unlock } from "react-feather"
import { UserMenu } from "./UserMenu"
import { CircleButton, CircleIconButton } from "./layouts"

const AVATAR_SIZE = "32px"
const LOCK_STATUS_SIZE = "18px"

export const AccountMenu = ({ isFixed = false }: { isFixed?: boolean }) => {
  // User
  const sso = useSsoService()
  const userInitials = sso.getInitials()
  const userFullName = sso.getFullName()
  const avatarConf = useRoleConf(avatarRoleConf)

  // Menu opening
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)
  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      {isFixed ? (
        <CircleIconButton
          onClick={handleClick}
          active={open}
          sx={avatarConf?.sx ?? undefined}
        >
          <Avatar sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}>
            {userInitials}
          </Avatar>
          {avatarConf?.displaySecureStatus && <SecureTokenIcon />}
        </CircleIconButton>
      ) : (
        <CircleButton
          onClick={handleClick}
          active={open}
          startIcon={
            <Avatar sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}>
              {userInitials}
            </Avatar>
          }
        >
          {userFullName}
          {avatarConf?.displaySecureStatus && <SecureTokenIcon />}
        </CircleButton>
      )}

      <UserMenu open={open} anchorEl={anchorEl} onClose={handleClose} />
    </>
  )
}

const Avatar = styled(MuiAvatar)(({ theme }) => ({
  // Prevents overrides by using a stronger selector
  [`&.${avatarClasses.root}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.header.containedButton.backgroundColor,
    fontSize: theme.typography.pxToRem(12),
  },
}))

const SecureTokenIcon = () => {
  const token = useStepUpToken()
  const tokenStatus = !!token.getStepUpToken()

  return (
    <Box
      sx={(theme) => ({
        width: LOCK_STATUS_SIZE,
        borderRadius: 9,
        backgroundColor: tokenStatus
          ? theme.palette.secondary.main
          : theme.palette.branding.main,
        height: LOCK_STATUS_SIZE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginLeft: theme.spacing(1),
      })}
    >
      {tokenStatus ? <Unlock size={10} /> : <Lock size={10} />}
    </Box>
  )
}

const adhConf = {
  displaySecureStatus: true,
  sx: (theme: Theme) => ({
    width: `calc(${AVATAR_SIZE} + ${LOCK_STATUS_SIZE} + ${theme.spacing(3)})`,
    borderRadius: `calc((${AVATAR_SIZE} + ${LOCK_STATUS_SIZE} + ${theme.spacing(3)}) / 2)`,
  }),
}

const avatarRoleConf: RoleStrategy<typeof adhConf> = {
  [ROLES.ADHERENT_HOLDER]: adhConf,
  [ROLES.ADHERENT_TEAM]: adhConf,
}
